import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Supplier } from '@common/services/commondata';
import { oDataService } from '@common/services/odata';
import { Pagination } from '@common/types/pagination.types';
import { environment } from 'environments/environment';
import { BehaviorSubject, forkJoin, Observable, tap, map, filter } from 'rxjs';
import _ from 'lodash';
import { count } from 'console';
@Injectable({ providedIn: 'root' })
export class SupplierService {
    // Private
    private _filters: BehaviorSubject<any> = new BehaviorSubject({});
    private _pagination: BehaviorSubject<Pagination> = new BehaviorSubject(null);
    private _product: BehaviorSubject<any | null> = new BehaviorSubject(null);
    private _products: BehaviorSubject<any[] | null> = new BehaviorSubject(null);
    private _supplier: BehaviorSubject<Supplier[]> = new BehaviorSubject<Supplier[]>(null);
    private controller: string = 'Supplier';
    private withJoin: string = 'country,currency,brands($expand=brand)';
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _oDataService: oDataService,
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set BehaviorSubject Supplier
     * @param value
     */
    setSupplier$(value: Supplier[]) {
        this._supplier.next(value);
    }

    /**
     * Getter for filters
     */
    get filters() {
        return this._filters.value;
    }

    /**
     * Setter for filters
     */
    set filters(values: any) {
        this._filters.next(values);
    }

    /**
     * Getter for Pagination
     */
    get pagination() {
        return this._pagination.value;
    }

    /**
     * Setter for Pagination
     */
    set pagination(values) {
        this._pagination.next(values);
    }

    /**
     * Getter for pagination
     */
    get pagination$(): Observable<Pagination> {
        return this._pagination.asObservable();
    }

    /**
     * Getter for product
     */
    get product$(): Observable<any> {
        return this._product.asObservable();
    }

    /**
     * Getter for products
     */
    get products$(): Observable<any[]> {
        return this._products.asObservable();
    }

    /**
     * Getter for Observable Supplier
     */
    get supplier$(): Observable<Supplier[]> {
        return this._supplier.asObservable();
    }

    /**
     * Getter for Supplier
     */
    get supplier(): Supplier[] {
        return this._supplier.value;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get products
     * @param reload
     */
    getProductsWithPagination(reload?: boolean): Observable<any> {
        if (reload) {
            this.pagination = {
                length: 0,
                page: 0,
                size: 100,
                sort: '',
                order: 'asc'
            }
            this.filters = {};
        }
        return forkJoin({
            products: this.getProducts(this.filters, this.pagination),
        }).pipe(
            tap((response) => {
                this._pagination.next({ ...this.pagination, length: response.products["@odata.count"] });
                this._products.next(response.products["value"]);
            })
        );
    }

    /**
     * Get products
     *
     * @param filters
     * @param pagination
     */
    getProducts(filters, pagination) {
        return this._httpClient.get<any[]>(`${environment.oDataUrl}/odata/` + this.controller + this._oDataService.setFilters(filters, pagination, this.withJoin));
    }

    getCommonData() {
        return this.getProducts({ isActive: true }, null).pipe(
             map((response) => {
                let resp =response["value"];
                resp.sort((a, b) => {
                    return a.code < b.code ? -1 : 1;
                 });
                 this._supplier.next(resp);
                 return resp;
             })
         );
     }
     getOrLoadSuppliers(): Observable<Supplier[]> {
        if (!this._supplier.value) {
            return this.getCommonData().pipe(
                map(() => this._supplier.value), // emit the loaded value
                filter(val => !!val)              // ensure not null
            );
        }
        return this._supplier.asObservable();

     }

    /**
     * download products
     * 
     * @param filters 
     */
    downloadProducts(filters): Observable<any[]> {
        return this._httpClient.get<any[]>(`${environment.oDataUrl}/odata/` + this.controller
            + this._oDataService.setFilters(filters, null, this.withJoin))
            .pipe(
                map((response) => {
                    return response["value"];
                })
            );
    }

    /**
     * Upload product
     * 
     * @param products
     */
    // uploadProduct(products) {
    //     return this._httpClient.post(`${environment.oDataUrl}/api/` + this.controller, products, {
    //         reportProgress: true,
    //         observe: 'events'
    //     });
    // }
uploadProduct(products) {
        let data = _.chain(products)
            .groupBy('code')
            .map((items, code) => {
                let product = ({
                    code: code,
                    name: items[0].name,
                    countryId: items[0].countryId,
                    currencyId: items[0].currencyId,
                    brands: items.map(x => { return { brandId: x.brandId }}),
                    isActive: items[0].isActive,
                })
                return product;
            }).value();
        return this._httpClient.post(
            `${environment.oDataUrl}/api/` + this.controller, data,
            {
                reportProgress: true,
                observe: 'events',
            }
        );
    }

    /**
     * Create product
     * 
     * @param products
     */
    createProduct(products) {
        this._httpClient.post(`${environment.oDataUrl}/api/` + this.controller, products).subscribe(() => {
            forkJoin({
                items: this.getProductsWithPagination(),
                commondata: this.getCommonData()
            }).subscribe();
        });
    }

    /**
     * Update product
     *
     * @param product
     */
    updateProduct(id: string, product: any) {
        this._httpClient.put(`${environment.oDataUrl}/api/` + this.controller + `/${id}`, product).subscribe(() => {
            forkJoin({
                items: this.getProductsWithPagination(),
                commondata: this.getCommonData()
            }).subscribe();
        });
    }

    /**
     * Code is not unique, Supplier has a composite key of BrandId and Code
     * 
     * You might want to use getSupplierIdByCodeBrand()
     * 
     * @param code
     */
    getSupplierIdByCode(code: string): number {
        let item = this.supplier.find(x => x.code === code)
        if (item == undefined || item.id == 0)
            return 0;
        return item.id;
    }

    getSupplierIdByCodeBrand(code: string, brandId: number): number {
        //old  //let item = this.supplier.find(x => x.code === code && x.brandId === brandId)

        let item = this.supplier.find(s =>
            s.code === code &&
            s.brands?.some(b => b.brandId === brandId)
        );
    
        if (!item || item.id === 0) {
            return 0;
        }
    
        return item.id;
    }
}
